/* eslint-disable */

import React from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { getTitleSuffix } from '@/constants';
import { isStockSufficient, getCardDiscount, CardLocale } from '@/src/types/card';
import { countryFlagSrc, getCountryName } from '@/src/helpers/countries';
import {
  CartItem as StyledCartItem,
  ItemImage,
  ItemInfo,
  ItemTitle,
  CountryIndicator,
  ItemPrice,
  DiscountedPrice,
  QuantityControl,
  QuantityButton,
  QuantityInput,
  RemoveButton
} from './CartStyledComponents';
import { CartItemWithCard } from './useCartLogic';
import { DeleteIcon } from '../icons';
import styles from './Cart.module.css';

interface CartItemProps {
  item: CartItemWithCard;
  index: number;
  cartPricing: any;
  allItems: CartItemWithCard[];
  onQuantityChange: (itemId: string, value: number, productIndex: number) => void;
  onRemoveItem: (index: number) => void;
  onProductClick: (card: any) => void;
}

const CartItemPrice: React.FC<{ item: CartItemWithCard; index: number; cartPricing: any; allItems: CartItemWithCard[] }> = ({ item, index, cartPricing, allItems }) => {
  const currency = item.currency;
  const priceStartIndex = allItems.slice(0, index).reduce((sum, prevItem) => sum + prevItem.quantity, 0);
  const itemPricing = cartPricing?.prices?.[priceStartIndex];
  
  return (
    <ItemPrice>
      {itemPricing ? (
        <DiscountedPrice>
          {currency} {itemPricing.price.toFixed(2)}
        </DiscountedPrice>
      ) : (
        <>{currency} {item.selectedValue.toFixed(2)}</>
      )}
    </ItemPrice>
  );
};

const CartItemTotalPrice: React.FC<{ item: CartItemWithCard; index: number; cartPricing: any; allItems: CartItemWithCard[] }> = ({ item, index, cartPricing, allItems }) => {
  const currency = item?.currency;
  const priceStartIndex = allItems.slice(0, index).reduce((sum, prevItem) => sum + prevItem.quantity, 0);
  const itemPricing = cartPricing?.prices?.[priceStartIndex];
  const totalValue = itemPricing ? itemPricing.price * item.quantity : item.selectedValue * item.quantity;

  return (
    <ItemPrice>
      {currency} {totalValue.toFixed(2)}
    </ItemPrice>
  );
};

export const CartItemComponent: React.FC<CartItemProps> = ({
  item,
  index,
  cartPricing,
  allItems,
  onQuantityChange,
  onRemoveItem,
  onProductClick
}) => {
  const t = useTranslations('cart');
  const locale = useLocale() as CardLocale;
  console.log(`🛒 Rendering cart item ${index}:`, item);

  return (
    <StyledCartItem key={`${item.productId}-${item.selectedValue}`}>
      <div className={styles.cartItemLayout}>
        <div className={styles.cartItemImageCenter}>
          <ItemImage
            card={item.card}
            locale={locale}
            onClick={() => item.card && onProductClick(item.card)}
            discount={item.card ? getCardDiscount(item.card) : 0}
          />
        </div>
        
        <ItemInfo>
          <ItemTitle 
            onClick={() => item.card && onProductClick(item.card)}
            style={{ cursor: 'pointer' }}
          >
            {`${item.card?.name}${getTitleSuffix()}`}
          </ItemTitle>
          
          <div className={styles.valueBadge}>
            <span className={styles.valueBadgeInner}>
              {t('item.value')} {item.currency} {item.selectedValue}
            </span>
          </div>
          
          <CountryIndicator>
            <span className={styles.countryIndicatorLabel}>{t('item.availableIn')}</span>
            <span className={styles.countryChipList}>
              {(item.variantCountries ?? item.card?.countries ?? []).map((country, idx) => {
                const countryCode = country?.toUpperCase();
                const countryName = getCountryName(countryCode);
                return (
                  <span key={`${countryCode}-${idx}`} className={styles.countryChip}>
                    <span>{countryName}</span>
                    {countryCode === 'WW' ? (
                      <span style={{ fontSize: '14px', lineHeight: 1 }}>🌍</span>
                    ) : (
                      <img
                        src={countryFlagSrc(countryCode)}
                        alt={countryName}
                        width={16}
                        height={12}
                        style={{ width: '16px', height: '12px', borderRadius: '2px' }}
                      />
                    )}
                  </span>
                );
              })}
            </span>
          </CountryIndicator>
          
          <div className={styles.mobilePriceRow}>
            <CartItemPrice item={item} index={index} cartPricing={cartPricing} allItems={allItems} />
          </div>
        </ItemInfo>
        
        <div className={styles.desktopControls}>
          <div className={styles.desktopPriceOnly}>
            <CartItemPrice item={item} index={index} cartPricing={cartPricing} allItems={allItems} />
          </div>
          
          <QuantityControl>
            <QuantityButton
              onClick={() => onQuantityChange(item.productId, item.quantity - 1, index)}
              disabled={item.quantity <= 1}
            >
              −
            </QuantityButton>
            <QuantityInput
              type="number"
              min="1"
              value={item.quantity}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => onQuantityChange(item.productId, parseInt(e.target.value) || 1, index)}
            />
            <QuantityButton
              onClick={() => onQuantityChange(item.productId, item.quantity + 1, index)}
              disabled={item.card && !isStockSufficient(item.card, item.selectedValue, item.currency, item.quantity + 1, item.country)}
            >
              +
            </QuantityButton>
          </QuantityControl>
          
          <div className={styles.controlsRight}>
            <CartItemTotalPrice item={item} index={index} cartPricing={cartPricing} allItems={allItems} />
            <RemoveButton onClick={() => onRemoveItem(index)}>
              <DeleteIcon style={{ width: 18, height: 18 }} />
              {t('item.remove')}
            </RemoveButton>
          </div>
        </div>
      </div>
    </StyledCartItem>
  );
};
